/**
 * WaitingQueue — the pinned pane's primary view: only the things waiting on a
 * person, in the server's order (longest wait first), split into two groups.
 *
 * Shared by ora and the Outlook add-in (`@product-app/common/workflows/…`);
 * there is no forked copy under the add-in tree (design Constraint 20).
 *
 * TWO GROUPS, NOT ONE FLAT LIST (operator, 2026-08-26: "I only want to bug
 * people when they are needed"):
 *
 *   "Needs you" — rows the server marked `needs_you`. This is the group the
 *                 waiting-on-you badge in `hitl-notifications` counts, so its
 *                 length IS the badge's number, visible directly beneath it.
 *   "Waiting"   — parked and visible, but not something the AM is being
 *                 interrupted for (a `warm_hitl` inside its grace window, or a
 *                 run on a workflow id outside NOTIFYING_WORKFLOW_IDS).
 *
 * `needs_you` is the SERVER's verdict, computed by `is_notifiable` in
 * `tools/waiting_queue.py` — the same module the badge's SQL comes from. This
 * component never re-derives it: a browser-side re-derivation is a second rule
 * by construction, and two rules that are never compared are one bug waiting.
 *
 * THREE LIST STATES, three distinct renders with distinct text:
 *   items          — the groups above
 *   nothing        — "Nothing waiting on you"
 *   could-not-load — "Couldn't refresh — last updated HH:MM"
 * A stale queue is NEVER presented as an accurate one, and a stale EMPTY queue
 * never renders the empty state: "nothing is waiting" and "we could not find
 * out what is waiting" are different sentences (AC-3).
 *
 * The could-not-load state has TWO wordings, chosen by `staleReason`, because
 * it has two causes. "Couldn't refresh" asserts that something went wrong; say
 * it while the poll is merely between ticks and the notice stops meaning
 * anything, which is worse than not showing one. A queue that is only OLD says
 * so plainly and accuses nothing.
 *
 * PLACEMENT: with rows on screen the notice rides at the END of the
 * "Needs you · N" heading line rather than in a banner above it — in a 440x920
 * pane vertical space is the scarce resource and a full-width banner costs a
 * visible queue row. With no rows there is no heading to share, and nothing
 * competing for the height, so there it stands alone.
 */

import React from "react";
import { formatTime } from "../formatTime";
import type { WaitingQueueStaleReason } from "../hooks/useWaitingQueue";
import type { WaitingQueueItem } from "../types";
import { WaitingQueueRow } from "./WaitingQueueRow";

export const QUEUE_EMPTY_COPY = "Nothing waiting on you";
export const QUEUE_NOTHING_NEEDED_COPY = "Nothing is needed from you";
export const QUEUE_NEEDS_YOU_HEADING = "Needs you";
export const QUEUE_WAITING_HEADING = "Waiting";

/**
 * The stale notice's words.
 *
 * `"error"` — a fetch FAILED: "Couldn't refresh — last updated 2:45 pm", or
 * without the clause when nothing has ever succeeded (nothing truthful to
 * name). `"age"` — nothing failed; the queue is simply older than the window:
 * "Last updated 2:45 pm", which reports without accusing.
 *
 * Defaults to the failure wording so an existing caller that passes no reason
 * keeps the stricter sentence rather than silently downgrading a real failure.
 */
export function staleCopy(
  lastSuccessAt: number | null | undefined,
  reason: WaitingQueueStaleReason | null = "error",
): string {
  const at = lastSuccessAt
    ? formatTime(new Date(lastSuccessAt).toISOString())
    : null;
  if (reason === "age") {
    return at ? `Last updated ${at}` : "Not updated yet";
  }
  return at
    ? `Couldn't refresh — last updated ${at}`
    : "Couldn't refresh — no successful update yet";
}

export interface WaitingQueueProps {
  /** The server's rows, in the server's order. */
  items: WaitingQueueItem[];
  /** True while the first fetch is in flight and nothing has arrived yet. */
  loading?: boolean;
  /**
   * True when the last fetch failed or nothing has succeeded for longer than
   * the staleness window. Renders the could-not-load state ABOVE any rows
   * already on screen — a failed fetch never clears them.
   */
  isStale?: boolean;
  /** Why the queue is stale, which selects the wording. Omitted defaults to
   *  the failure sentence — never downgrade an unexplained stale to "age". */
  staleReason?: WaitingQueueStaleReason | null;
  /** Epoch ms of the last SUCCESSFUL fetch — named in the stale copy. */
  lastSuccessAt?: number | null;
  /** Raised to the host. The queue never navigates by itself. */
  onOpenItem: (instanceId: string) => void;
  /** Injected only so tests and the render probe can pin "now". */
  now?: Date;
  /** Override the empty-queue sentence (AC-7 when nothing is running either). */
  emptyCopy?: string;
}

function GroupHeading({
  heading,
  count,
  testId,
  note,
}: {
  heading: string;
  count: number;
  testId: string;
  /** Rendered at the END of the heading line. The pane is a 440x920 strip and
   *  vertical space is the scarce resource, so a short status shares this row
   *  instead of claiming a full-width one above the queue. */
  note?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="mb-1 flex items-baseline justify-between gap-2">
      <h3
        data-testid={`${testId}-heading`}
        className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.07em] text-primary-gray-light-500"
      >
        {heading} · {count}
      </h3>
      {note}
    </div>
  );
}

function Group({
  heading,
  items,
  onOpenItem,
  now,
  testId,
  alwaysShow = false,
  featureFirst = false,
  note,
}: {
  heading: string;
  items: WaitingQueueItem[];
  onOpenItem: (instanceId: string) => void;
  now?: Date;
  testId: string;
  alwaysShow?: boolean;
  featureFirst?: boolean;
  note?: React.ReactNode;
}): React.ReactElement | null {
  if (items.length === 0 && !alwaysShow) return null;
  return (
    <section data-testid={testId} className="flex flex-col">
      {/* The count lives in the header TEXT so the correspondence with the
          badge is visible rather than inferred, and so group membership is
          never carried by colour or position alone. */}
      <GroupHeading
        heading={heading}
        count={items.length}
        testId={testId}
        note={note}
      />
      {items.length === 0 ? null : (
        <ul
          aria-label={heading}
          className="flex max-h-44 list-none flex-col gap-2 overflow-y-auto p-0"
        >
          {items.map((item, index) => (
            <WaitingQueueRow
              key={item.instance_id}
              item={item}
              onOpenItem={onOpenItem}
              now={now}
              featured={featureFirst && index === 0}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

export function WaitingQueue({
  items,
  loading = false,
  isStale = false,
  staleReason = "error",
  lastSuccessAt = null,
  onOpenItem,
  now,
  emptyCopy = QUEUE_EMPTY_COPY,
}: WaitingQueueProps): React.ReactElement {
  // Order is the SERVER's order. This partition preserves it inside each
  // group and the component does NOT re-sort — one ordering rule (the SQL)
  // rather than two that can disagree.
  const needsYou = items.filter((i) => i.needs_you);
  const waiting = items.filter((i) => !i.needs_you);

  // Shares the "Needs you · N" line rather than claiming a row of its own.
  // Truncates, with the full sentence on hover, so a long status can never
  // wrap and grow the header it is sitting in.
  const staleNote = isStale ? (
    <span
      role="status"
      data-testid="waiting-queue-stale"
      title={staleCopy(lastSuccessAt, staleReason)}
      className="min-w-0 truncate text-[10px] font-normal text-primary-gray-light-500"
    >
      {staleCopy(lastSuccessAt, staleReason)}
    </span>
  ) : null;

  return (
    // Rows stay legible at 320px: one column, no fixed widths, no table.
    <div
      className="flex w-full flex-col gap-3"
      data-testid="waiting-queue"
      data-item-count={items.length}
    >
      {items.length > 0 ? (
        <>
          <Group
            heading={QUEUE_NEEDS_YOU_HEADING}
            items={needsYou}
            onOpenItem={onOpenItem}
            now={now}
            testId="waiting-queue-needs-you"
            alwaysShow
            featureFirst
            note={staleNote}
          />
          <Group
            heading={QUEUE_WAITING_HEADING}
            items={waiting}
            onOpenItem={onOpenItem}
            now={now}
            testId="waiting-queue-waiting"
          />
        </>
      ) : loading && !isStale ? (
        <p
          data-testid="waiting-queue-loading"
          className="px-3 py-3 text-center text-sm text-primary-gray-light-500"
        >
          Checking what needs you…
        </p>
      ) : isStale ? (
        // No heading row to share, and no rows competing for the height, so
        // here the notice stands on its own. Still never the empty state
        // (AC-3): a queue we could not read is not a queue known to be empty.
        <p
          role="status"
          data-testid="waiting-queue-stale"
          title={staleCopy(lastSuccessAt, staleReason)}
          className="px-3 py-3 text-center text-xs text-primary-gray-light-500"
        >
          {staleCopy(lastSuccessAt, staleReason)}
        </p>
      ) : (
        // Reached only when a fetch SUCCEEDED and returned nothing.
        <section data-testid="waiting-queue-needs-you">
          <GroupHeading
            heading={QUEUE_NEEDS_YOU_HEADING}
            count={0}
            testId="waiting-queue-needs-you"
          />
          <p
            data-testid="waiting-queue-empty"
            className="rounded-[10px] border border-dashed border-primary-gray-light-200 px-3 py-3 text-center text-sm text-primary-gray-light-600"
          >
            {emptyCopy}
          </p>
        </section>
      )}
    </div>
  );
}

export default WaitingQueue;
